import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Flame } from "lucide-react";
import { toast } from "sonner";
import { SENDER_DOMAIN } from "@/lib/email-config";
import { getAuthRedirectBase } from "@/lib/auth-redirect";

const RESEND_COOLDOWN_SECONDS = 60;
const COOLDOWN_KEY_PREFIX = "lge:check-email:cooldown:";

function cooldownKey(email: string) {
  return `${COOLDOWN_KEY_PREFIX}${email.toLowerCase()}`;
}

function readPersistedCooldown(email: string): number {
  if (!email) return 0;
  try {
    const raw = localStorage.getItem(cooldownKey(email));
    if (!raw) return 0;
    const until = parseInt(raw, 10);
    if (!Number.isFinite(until)) return 0;
    const remaining = Math.ceil((until - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
}

function writePersistedCooldown(email: string, seconds: number) {
  if (!email) return;
  try {
    if (seconds <= 0) {
      localStorage.removeItem(cooldownKey(email));
      return;
    }
    localStorage.setItem(cooldownKey(email), String(Date.now() + seconds * 1000));
  } catch {
    // ignore — cooldown will fall back to in-memory state only
  }
}

export default function CheckEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const email =
    (location.state as { email?: string } | null)?.email ??
    user?.email ??
    "";
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  // If the user lands here with no context at all, send them back to signup.
  useEffect(() => {
    if (!email) navigate("/signup", { replace: true });
  }, [email, navigate]);

  // If the signed-in user has already confirmed their email, don't keep them
  // on this page — bounce to the dashboard. Must check it's the SAME email
  // this page is for, not just any confirmed session — otherwise a stale
  // session left over from a different account (e.g. a shared/reused
  // browser) would silently bounce someone into that other account's
  // dashboard instead of showing them "check your email".
  useEffect(() => {
    if (user && user.email?.toLowerCase() === email.toLowerCase() && (user.email_confirmed_at || (user as any).confirmed_at)) {
      navigate("/", { replace: true });
    }
  }, [user, email, navigate]);

  // Restore any persisted cooldown for this email so a refresh / new tab
  // doesn't bypass our 60s rate limit.
  useEffect(() => {
    if (!email) return;
    setCooldown(readPersistedCooldown(email));
  }, [email]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => {
      setCooldown(c => {
        const next = c - 1;
        if (next <= 0 && email) writePersistedCooldown(email, 0);
        return next;
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [cooldown, email]);

  async function handleResend() {
    if (!email || cooldown > 0 || resending) return;
    setResending(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: getAuthRedirectBase() },
    });
    setResending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Confirmation email re-sent");
    setCooldown(RESEND_COOLDOWN_SECONDS);
    writePersistedCooldown(email, RESEND_COOLDOWN_SECONDS);
  }

  if (!email) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Flame className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">Check your inbox</CardTitle>
          <p className="text-sm text-muted-foreground">One last step to activate your account</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
            <Mail className="mt-0.5 h-5 w-5 text-primary" />
            <div className="text-sm">
              <p className="font-medium text-foreground">We sent a confirmation link to</p>
              <p className="break-all text-foreground">{email}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                Click the link in that email to verify your address, then sign in.
              </p>
            </div>
          </div>

          <div className="space-y-2 text-xs text-muted-foreground">
            <p>
              Didn't get it? The email comes from{" "}
              <span className="font-medium text-foreground">noreply@{SENDER_DOMAIN}</span> —
              check your spam folder, or resend it below.
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleResend}
            disabled={cooldown > 0 || resending}
          >
            {resending
              ? "Resending…"
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : "Resend confirmation email"}
          </Button>

          <div className="flex flex-col gap-1 pt-2 text-center text-sm">
            <Link to="/login" className="font-medium text-primary hover:underline">
              Already confirmed? Sign in
            </Link>
            <Link to="/signup" className="text-muted-foreground hover:underline">
              Wrong email? Sign up again
            </Link>
            {user && (
              <button
                type="button"
                onClick={async () => { await signOut(); navigate("/login", { replace: true }); }}
                className="text-muted-foreground hover:underline"
              >
                Sign out
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
